import { useQueries } from "@tanstack/react-query";
import sessionsService, { type AttendanceStatusCounts } from "@/features/sessions/services/sessionsService";
import DURATIONS from "@/shared/constants/durations";
import { sessionsKeys } from "./sessionsQueryKeys";

/**
 * Fetches attendance status counts (attended, absent, cancelled, unconfirmed) for multiple sessions.
 * Uses Firestore count() aggregations via the service (no attendance/user document downloads).
 */
export function useAttendanceStatusCounts(sessionIds: string[]) {
    const statusQueries = useQueries({
        queries: sessionIds.map((sessionId) => ({
            enabled: sessionIds.length > 0,
            queryFn: (): Promise<AttendanceStatusCounts> => sessionsService.getAttendanceStatusCounts(sessionId),
            queryKey: [...sessionsKeys.attendance.session(sessionId), "status-counts"],
            staleTime: DURATIONS.FIVE_MINUTES,
        })),
    });

    const attendedCounts = new Map<string, number>();
    const absentCounts = new Map<string, number>();
    const cancelledCounts = new Map<string, number>();
    const unconfirmedCounts = new Map<string, number>();

    sessionIds.forEach((sessionId, index) => {
        const result = statusQueries[index]?.data ?? { absent: 0, attended: 0, cancelled: 0, unconfirmed: 0 };
        attendedCounts.set(sessionId, result.attended);
        absentCounts.set(sessionId, result.absent);
        cancelledCounts.set(sessionId, result.cancelled);
        unconfirmedCounts.set(sessionId, result.unconfirmed);
    });

    return {
        absentCounts,
        attendedCounts,
        cancelledCounts,
        isError: statusQueries.some((q) => q.isError),
        isLoading: statusQueries.some((q) => q.isLoading),
        unconfirmedCounts,
    };
}
